import { create } from 'zustand';

/**
 * Venue Store - Manages venue data and saved venues
 * 
 * Features:
 * - Mock venue data
 * - Get venue by ID
 * - Get similar venues
 * - Toggle saved venues
 * - Check if venue is saved
 */

// Mock venue data
const mockVenues = [
  {
    id: '1',
    name: 'Metro Sports Center',
    rating: 4.8,
    reviewCount: 124,
    isOpen: true,
    hours: '6:00 AM - 10:00 PM',
    address: '456 Metro Ave, New York, NY 10001',
    phone: '(555) 123-4567',
    website: 'https://metrosports.example.com',
    coordinates: { lat: 40.7589, lng: -73.9851 },
    distance: '0.5 mi',
    sports: [
      { name: 'Basketball', courts: 4 },
      { name: 'Volleyball', courts: 2 },
      { name: 'Badminton', courts: 6 },
    ],
    amenities: [
      { name: 'Parking', available: true },
      { name: 'Showers', available: true },
      { name: 'Lockers', available: true },
      { name: 'Equipment Rental', available: true },
      { name: 'Cafe', available: false },
      { name: 'WiFi', available: true },
    ],
    description: 'Metro Sports Center is a premier multi-sport facility in the heart of the city. Featuring professional-grade courts, modern amenities, and a welcoming community of athletes. Whether you\'re looking for a pickup game or competitive league play, Metro Sports has you covered. Our facilities are maintained to the highest standards with climate control and professional lighting.',
    images: [1, 2, 3, 4, 5, 6],
    upcomingEvents: [
      { id: 'e1', title: 'Basketball Pickup', date: 'Sat, Dec 16', time: '2:00 PM', spotsLeft: 4 },
      { id: 'e2', title: 'Volleyball League', date: 'Sun, Dec 17', time: '10:00 AM', spotsLeft: 2 },
      { id: 'e3', title: 'Youth Basketball Camp', date: 'Mon, Dec 18', time: '9:00 AM', spotsLeft: 15 },
      { id: 'e4', title: 'Badminton Tournament', date: 'Tue, Dec 19', time: '6:00 PM', spotsLeft: 8 },
    ],
    reviews: [
      { id: 'r1', author: 'John D.', rating: 5, date: '2 weeks ago', text: 'Great facility! Courts are well maintained and the staff is friendly. The basketball courts have excellent lighting and the floors are in perfect condition. I\'ve been coming here for pickup games every weekend.' },
      { id: 'r2', author: 'Sarah M.', rating: 4, date: '1 month ago', text: 'Love playing here. Parking can be tricky during peak hours though. The volleyball nets are high quality and the court dimensions are regulation size. Would definitely recommend for serious players.' },
      { id: 'r3', author: 'Mike R.', rating: 5, date: '2 months ago', text: 'Best sports center in the area. The badminton courts are amazing with great airflow and lighting. Staff is super helpful with equipment rental too.' },
      { id: 'r4', author: 'Emily W.', rating: 4, date: '3 months ago', text: 'Clean facilities and good atmosphere. The only downside is the limited cafe options, but there are plenty of places nearby to grab food.' },
    ],
  },
  {
    id: '2',
    name: 'Central Park Tennis Club',
    rating: 4.6,
    reviewCount: 89,
    isOpen: true,
    hours: '7:00 AM - 9:00 PM',
    address: '123 Park West, New York, NY 10019',
    phone: '(555) 987-6543',
    website: 'https://centraltennis.example.com',
    coordinates: { lat: 40.7829, lng: -73.9654 },
    distance: '1.2 mi',
    sports: [
      { name: 'Tennis', courts: 12 },
    ],
    amenities: [
      { name: 'Parking', available: true },
      { name: 'Showers', available: true },
      { name: 'Lockers', available: true },
      { name: 'Equipment Rental', available: true },
      { name: 'Cafe', available: true },
      { name: 'WiFi', available: true },
    ],
    description: 'Central Park Tennis Club offers world-class tennis facilities with both indoor and outdoor courts. Our professional instructors and well-maintained surfaces provide the perfect environment for players of all skill levels.',
    images: [1, 2, 3, 4],
    upcomingEvents: [
      { id: 'e5', title: 'Tennis Clinic', date: 'Sat, Dec 16', time: '10:00 AM', spotsLeft: 6 },
      { id: 'e6', title: 'Doubles Tournament', date: 'Sun, Dec 17', time: '1:00 PM', spotsLeft: 3 },
    ],
    reviews: [
      { id: 'r5', author: 'David L.', rating: 5, date: '1 week ago', text: 'Beautiful courts and excellent coaching staff. The clay courts are perfectly maintained.' },
      { id: 'r6', author: 'Lisa K.', rating: 4, date: '3 weeks ago', text: 'Great location and facilities. Booking can be competitive on weekends though.' },
    ],
  },
  {
    id: '3',
    name: 'Urban Soccer Arena',
    rating: 4.5,
    reviewCount: 67,
    isOpen: false,
    hours: '8:00 AM - 11:00 PM',
    address: '789 Sports Blvd, New York, NY 10002',
    phone: '(555) 456-7890',
    website: 'https://urbansoccer.example.com',
    coordinates: { lat: 40.7282, lng: -73.9942 },
    distance: '0.8 mi',
    sports: [
      { name: 'Soccer', courts: 3 },
      { name: 'Football', courts: 1 },
    ],
    amenities: [
      { name: 'Parking', available: true },
      { name: 'Showers', available: true },
      { name: 'Lockers', available: false },
      { name: 'Equipment Rental', available: true },
      { name: 'Cafe', available: true },
      { name: 'WiFi', available: true },
    ],
    description: 'Urban Soccer Arena features state-of-the-art artificial turf fields perfect for soccer and flag football. Our climate-controlled facility ensures comfortable play year-round.',
    images: [1, 2, 3, 4, 5],
    upcomingEvents: [
      { id: 'e7', title: 'Youth Soccer League', date: 'Sat, Dec 16', time: '9:00 AM', spotsLeft: 12 },
      { id: 'e8', title: 'Pickup Soccer', date: 'Sun, Dec 17', time: '3:00 PM', spotsLeft: 5 },
    ],
    reviews: [
      { id: 'r7', author: 'Carlos M.', rating: 5, date: '2 weeks ago', text: 'Best turf in the city! The fields are always in great condition.' },
      { id: 'r8', author: 'Anna P.', rating: 4, date: '1 month ago', text: 'Great facility but wish they had more locker space.' },
    ],
  },
  {
    id: '4',
    name: 'Elite Basketball Academy',
    rating: 4.9,
    reviewCount: 156,
    isOpen: true,
    hours: '5:00 AM - 11:00 PM',
    address: '321 Court St, New York, NY 10013',
    phone: '(555) 234-5678',
    website: 'https://elitebasketball.example.com',
    coordinates: { lat: 40.7209, lng: -74.0007 },
    distance: '1.5 mi',
    sports: [
      { name: 'Basketball', courts: 8 },
    ],
    amenities: [
      { name: 'Parking', available: true },
      { name: 'Showers', available: true },
      { name: 'Lockers', available: true },
      { name: 'Equipment Rental', available: true },
      { name: 'Cafe', available: true },
      { name: 'WiFi', available: true },
    ],
    description: 'Elite Basketball Academy is the premier destination for basketball enthusiasts. With 8 regulation courts, professional training programs, and tournament hosting capabilities, we cater to everyone from beginners to pros.',
    images: [1, 2, 3, 4, 5, 6, 7],
    upcomingEvents: [
      { id: 'e9', title: 'Pro Training Camp', date: 'Sat, Dec 16', time: '8:00 AM', spotsLeft: 8 },
      { id: 'e10', title: '3v3 Tournament', date: 'Sun, Dec 17', time: '12:00 PM', spotsLeft: 2 },
      { id: 'e11', title: 'Skills Workshop', date: 'Mon, Dec 18', time: '6:00 PM', spotsLeft: 15 },
    ],
    reviews: [
      { id: 'r9', author: 'James H.', rating: 5, date: '3 days ago', text: 'Unbelievable facility! The courts are NBA-quality and the staff really knows their stuff.' },
      { id: 'r10', author: 'Tina S.', rating: 5, date: '1 week ago', text: 'My kids love the youth programs. Great coaches and excellent facilities.' },
    ],
  },
  {
    id: '5',
    name: 'Aquatic Sports Complex',
    rating: 4.4,
    reviewCount: 78,
    isOpen: true,
    hours: '6:00 AM - 9:00 PM',
    address: '555 Pool Lane, New York, NY 10016',
    phone: '(555) 876-5432',
    website: 'https://aquaticsports.example.com',
    coordinates: { lat: 40.7484, lng: -73.9857 },
    distance: '2.0 mi',
    sports: [
      { name: 'Swimming', courts: 8 },
    ],
    amenities: [
      { name: 'Parking', available: true },
      { name: 'Showers', available: true },
      { name: 'Lockers', available: true },
      { name: 'Equipment Rental', available: false },
      { name: 'Cafe', available: false },
      { name: 'WiFi', available: true },
    ],
    description: 'Aquatic Sports Complex features Olympic-sized pools and diving facilities. Perfect for competitive swimming, water polo, and recreational swimming.',
    images: [1, 2, 3],
    upcomingEvents: [
      { id: 'e12', title: 'Swim Meet', date: 'Sat, Dec 16', time: '7:00 AM', spotsLeft: 20 },
      { id: 'e13', title: 'Water Polo Practice', date: 'Sun, Dec 17', time: '4:00 PM', spotsLeft: 6 },
    ],
    reviews: [
      { id: 'r11', author: 'Michael P.', rating: 4, date: '2 weeks ago', text: 'Great pools but the locker rooms could use an upgrade.' },
      { id: 'r12', author: 'Rachel G.', rating: 5, date: '1 month ago', text: 'Love swimming here early mornings. Very peaceful and well-maintained.' },
    ],
  },
  {
    id: '6',
    name: 'Multi-Sport Heights',
    rating: 4.3,
    reviewCount: 45,
    isOpen: true,
    hours: '7:00 AM - 10:00 PM',
    address: '888 Heights Ave, New York, NY 10025',
    phone: '(555) 345-6789',
    website: 'https://multisportheights.example.com',
    coordinates: { lat: 40.7989, lng: -73.9442 },
    distance: '2.5 mi',
    sports: [
      { name: 'Basketball', courts: 2 },
      { name: 'Volleyball', courts: 2 },
      { name: 'Badminton', courts: 4 },
    ],
    amenities: [
      { name: 'Parking', available: false },
      { name: 'Showers', available: true },
      { name: 'Lockers', available: true },
      { name: 'Equipment Rental', available: true },
      { name: 'Cafe', available: false },
      { name: 'WiFi', available: true },
    ],
    description: 'Multi-Sport Heights offers affordable access to multiple sports in a community-focused environment. Great for casual players and families.',
    images: [1, 2, 3, 4],
    upcomingEvents: [
      { id: 'e14', title: 'Community Tournament', date: 'Sat, Dec 16', time: '11:00 AM', spotsLeft: 10 },
    ],
    reviews: [
      { id: 'r13', author: 'Tom B.', rating: 4, date: '3 weeks ago', text: 'Good value for money. Courts are decent and the community is welcoming.' },
    ],
  },
];

export const useVenueStore = create((set, get) => ({
  // State
  venues: mockVenues,
  savedVenueIds: new Set(),
  
  // Getters
  getVenueById: (id) => {
    return get().venues.find(venue => venue.id === id) || null;
  },
  
  getSimilarVenues: (currentId, limit = 5) => {
    const currentVenue = get().getVenueById(currentId);
    if (!currentVenue) return [];
    
    // Get venues with similar sports, excluding current venue
    return get().venues
      .filter(venue => venue.id !== currentId)
      .map(venue => {
        // Calculate similarity score based on shared sports
        const sharedSports = venue.sports.filter(sport => 
          currentVenue.sports.some(s => s.name === sport.name)
        ).length;
        
        return {
          ...venue,
          similarityScore: sharedSports,
        };
      })
      .sort((a, b) => {
        // Sort by similarity score, then by rating
        if (b.similarityScore !== a.similarityScore) {
          return b.similarityScore - a.similarityScore;
        }
        return b.rating - a.rating;
      })
      .slice(0, limit);
  },
  
  getAllVenues: () => {
    return get().venues;
  },
  
  getSavedVenues: () => {
    const { venues, savedVenueIds } = get();
    return venues.filter(venue => savedVenueIds.has(venue.id));
  },
  
  // Actions
  toggleSaved: (venueId) => {
    const { savedVenueIds } = get();
    const newSavedIds = new Set(savedVenueIds);
    
    if (newSavedIds.has(venueId)) {
      newSavedIds.delete(venueId);
      set({ savedVenueIds: newSavedIds });
      return false;
    } else {
      newSavedIds.add(venueId);
      set({ savedVenueIds: newSavedIds });
      return true;
    }
  },
  
  isVenueSaved: (venueId) => {
    return get().savedVenueIds.has(venueId);
  },
  
  // For filtering/search (future implementation)
  filterVenues: (filters) => {
    const { venues } = get();
    return venues.filter(venue => {
      if (filters.sport && !venue.sports.some(s => s.name === filters.sport)) {
        return false;
      }
      if (filters.openNow && !venue.isOpen) {
        return false;
      }
      if (filters.minRating && venue.rating < filters.minRating) {
        return false;
      }
      return true;
    });
  },
}));

export default useVenueStore;
